import { computed } from 'vue'
// types
import type { ComputedRef } from 'vue'
import type { BlockSectionLayoutProps } from '@ts-blocks/block-section-layout/interfaces'
import type { SectionTutorsProps } from '@ts-sections/section-tutors/interfaces'
// hooks
import { useEventsSection } from '@hooks/events/sections/events-section.hook'

interface TutorCardMotion {
  initial: { opacity: number, y: number }
  visibleOnce: { opacity: number, y: number, transition: { duration: number, delay: number } }
}

interface SectionTutorsHookOptions {
  section: () => SectionTutorsProps
}

interface SectionTutorsHookReturn {
  refTitle: ComputedRef<string>
  refDescription: ComputedRef<string>
  refBackgroundColor: ComputedRef<NonNullable<BlockSectionLayoutProps['backgroundColor']>>
  getCardMotion: (index: number) => TutorCardMotion
  handleClickPrev: () => void
  handleClickNext: () => void
}

export const useSectionTutors = ({ section }: SectionTutorsHookOptions): SectionTutorsHookReturn => {
  const { trackSectionEvent } = useEventsSection({ sectionName: 'tutors' })

  const refLayout = computed<BlockSectionLayoutProps | null>(() => section().layout)

  const refTitle = computed<string>(() => refLayout.value?.title ?? '')
  const refDescription = computed<string>(() => refLayout.value?.description ?? '')

  const refBackgroundColor = computed<NonNullable<BlockSectionLayoutProps['backgroundColor']>>(() => (
    refLayout.value?.backgroundColor ?? 'tertiary'
  ))

  const getCardMotion = (index: number): TutorCardMotion => ({
    initial: { opacity: 0, y: 40 },
    visibleOnce: { opacity: 1, y: 0, transition: { duration: 500, delay: index * 120 } },
  })

  const handleClickPrev = (): void => {
    trackSectionEvent('Button_click', { name: 'slider_prev' })
  }

  const handleClickNext = (): void => {
    trackSectionEvent('Button_click', { name: 'slider_next' })
  }

  return {
    refTitle,
    refDescription,
    refBackgroundColor,
    getCardMotion,
    handleClickPrev,
    handleClickNext,
  }
}
